<template>
  <div class="group-filter-block">
    <v-text-field
      v-if="!isLocked"
      :label="fieldLabel"
      class="mb-3"
      readonly
      :value="filter.field.text"
      @click.native="$emit('showModal', 'FieldSelect')"
      :disabled="isLocked || disableEditing"
    />

    <div :class="{ 'flex-row': showOperator }">
      <v-select
        v-if="showOperator"
        class="operator-select mb-3"
        :label="operatorLabel"
        :value="filter.operator"
        :items="operatorOptions"
        :menu-props="{ maxHeight: 366, closeOnContentClick: true }"
        :rules="[(v) => validationRules.checkRequired(v, 'Operator')]"
        :disabled="disableEditing"
        item-text="label"
        return-object
      >
        <template v-slot:item="{ item }">
          <v-list-item
            class="operator-select__option"
            :class="{
              'v-list-item--active primary--text': isOperatorActive(item),
            }"
            @click="changeOperator(item)"
          >
            <v-list-item-title>
              {{ item.label }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-select>

      <div v-if="showValueInput">
        <v-combobox
          v-if="isSearchableField"
          :label="valueLabel"
          v-model="filter.value"
          :search-input.sync="search"
          :rules="[(v) => validationRules.checkRequired(v, 'Value')]"
          :items="[]"
          :disabled="(!filter.field && !filter.operator) || disableEditing"
          :multiple="filter.operator && filter.operator.list"
          :small-chips="filter.operator && filter.operator.list"
          @click.native.prevent.stop="selectValue"
          hide-selected
        />

        <form-field
          v-else
          v-model="filterValue"
          :schema="fieldSchema"
          :disabled="(!filter.field && !filter.operator) || disableEditing"
          field="value"
        />
      </div>
    </div>

    <v-btn
      v-if="activity && showDeleteButton"
      class="delete-field-btn"
      fab
      small
      @click="$emit('delete')"
    >
      <v-icon color="appMain">clear</v-icon>
    </v-btn>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import FormField from '@/components/formFields/FormField';
import {
  getSection,
  makeOperatorOptions,
  searchableFields,
  namesSuggestionsTypes,
} from './groupHelpers';

import validationRules from '@/lib/validationRules';
import isEqual from 'lodash/isEqual';
import capitalize from 'lodash/capitalize';

export default {
  name: 'GroupFilterBlock',
  components: {
    FormField,
  },
  props: {
    filter: {
      type: Object,
      default: () => ({}),
    },
    filtersGroup: {
      type: Array,
      default: () => [],
    },
    activity: String,
    tagGroup: Boolean,
    disableEditing: Boolean,
    teamFilterOff: Boolean,
  },
  data() {
    return {
      search: '',
      suggestions: [],
      validationRules,
      lessonSpecialFields: ['answer', 'item'],
    };
  },
  watch: {
    ['filter.field']() {
      this.setDefaultOperator();
    },
    ['filter.value'](newVal, oldVal) {
      if (newVal && !isEqual(newVal, oldVal)) {
        this.checkNames();
      }
    },
  },
  computed: {
    ...mapGetters('fields', ['attributesFields']),
    fieldLabel() {
      if (this.isVectorRecruiting) {
        return 'Select Field';
      }

      return 'Select Field or Activity';
    },
    filterValue: {
      get() {
        if (this.isExpression) {
          return (this.filter.value || '').replace('~', '');
        }
        return this.filter.value;
      },
      set(value) {
        if (this.isExpression && value && !value.startsWith('~')) {
          value = `~${value}`;
        }
        this.filter.value = value;
      },
    },
    isExpression() {
      return this.filter?.operator?.expression;
    },
    getValueLabel() {
      const field = this.filter.field;
      if (field && field.field === 'progress') {
        return 'Percent, %';
      }
      return 'Value';
    },
    isLocked() {
      let filterField = this.filter.field;
      return (filterField && filterField.fixed) || this.tagGroup;
    },
    valueLabel() {
      if (!this.isLocked) return 'Value';
      return this.filter.field.text;
    },
    showOperator() {
      return !this.isEventRow;
      // let types = ['int', 'date', 'time']
      // return this.filter.field && (types.includes(this.filter.field.type) || this.isEventRow)
    },
    operatorLabel() {
      if (!this.filter.field) return '';
      if (this.fieldSchema.type === 'Date') return 'When';
      if (this.filter.field.field === 'event') return 'Operator';
      return `${this.filter.field.text}`;
    },
    operatorOptions() {
      if (!this.filter.field) return [];
      const { operator, field } = this.filter || {};
      const isTracking = this.type === 'additional';
      return makeOperatorOptions(field, isTracking, operator);
    },
    showValueInput() {
      const operator = this.filter.operator?.operator || this.filter.operator;
      return !!(this.filter.field && operator && operator !== '$exists');
    },
    isSearchableField() {
      const { type, field } = this.filter?.field || {};
      return [type, field].some((i) => searchableFields.includes(i));
    },
    checkFixed() {
      let filterField = this.filter.field;
      if (!filterField) return false;
      if (filterField.fixed) {
        const isTrigger =
          this.eventType === 'event' && filterField.value.includes('.trigger');
        return !isTrigger;
      }
      return false;
    },
    getFieldOptions() {
      if (!this.filter.field || !this.checkFixed) {
        return this.preparedFields;
      } else {
        return [this.filter.field];
      }
    },
    fieldSchema() {
      const field = this.filter.field;
      const operator = this.filter.operator || {};

      return {
        group: '',
        title: operator.unit
          ? `${capitalize(operator.unit)} Ago`
          : this.getValueLabel,
        type: this.getFieldType(field.type, operator),
        options: field.type === 'bool' ? ['true', 'false'] : [],
        uppercase: !!operator.list,
        validators: ['required'],
      };
    },
    isEventRow() {
      return this.filter.field && this.filter.field.field === 'event';
    },
    showDeleteButton() {
      const sectionFields = getSection(this.activity);
      const field = this.filter.field.field;
      return (
        !sectionFields.some((i) => Object.keys(i)[0].includes(field)) &&
        !this.disableEditing
      );
    },
  },
  methods: {
    ...mapActions([
      'loadActivitySuggestions',
      'loadLessonMeta',
      'loadContactNameSuggestions',
      'loadContactSuggestions',
    ]),

    isOperatorActive(item) {
      return item.value === this.filter.operator.value;
    },

    getFieldType(type, operator) {
      if (type === 'bool') return 'ButtonsGroup';
      if (operator.list) return 'Tags';
      if (type === 'int' || operator.type === 'number') return 'Number';
      if (type === 'date' || type === 'time' || operator.type === 'date') {
        return operator.type === 'string' ? 'Text' : 'Date';
      }
      return 'Select';
    },

    // extract field value from an array of fields
    getFieldValue(f) {
      let fieldObj = this.filtersGroup.find(
        ({ field }) => field && field.field === f
      );
      return fieldObj ? fieldObj.value.value || fieldObj.value : '';
    },

    checkNames() {
      const field = this.filter.field ? this.filter.field.field : '';

      if (this.lessonSpecialFields.includes(field)) {
        this.findTextForLessonItems(field);
        return;
      }

      const nameSuggestionType = namesSuggestionsTypes[field];

      if (nameSuggestionType) {
        this.loadActivitySuggestions({
          type: nameSuggestionType,
        }).then((names) => {
          this.findNameSuggestion(names);
        });
      }

      const fieldType = this.filter.field.type;
      let filterValue = this.filter.value;
      filterValue = Array.isArray(filterValue) ? filterValue : [filterValue];

      const relationCallback = (names) => this.findNameSuggestion(names);

      if (fieldType === 'related_contacts') {
        filterValue.forEach((value) => {
          this.loadContactNameSuggestions({
            filters: { id: value },
          }).then(relationCallback);
        });
      }

      if (fieldType === 'relation') {
        filterValue.forEach((value) => {
          value = typeof value === 'object' ? value.value || '' : value;
          this.loadContactSuggestions({
            field: this.filter.field.value,
            query: value,
            teamFilterOff: this.teamFilterOff,
          }).then(relationCallback);
        });
      }
    },

    // find text for lesson items (by uuid) & answer (by idx)
    findTextForLessonItems(field) {
      const lessonId = this.getFieldValue('lesson');
      const itemId = this.getFieldValue('item');
      if (!lessonId) return;

      this.loadLessonMeta(lessonId).then((items = []) => {
        const lessonItem = items.find((i) => i.uuid === itemId);
        if (!lessonItem) return;

        // lesson item
        if (field === 'item') {
          this.filter.value = {
            text: lessonItem.summary || lessonItem.type,
            value: lessonItem.uuid,
          };
        }

        // answer inside lesson item
        if (field === 'answer' && lessonItem.event_properties.answer) {
          const answer = lessonItem.event_properties.answer;
          const answerVal = this.filter.value.value || this.filter.value;

          if (answer.range) {
            this.filter.value = {
              text: `${answerVal} Checked`,
              value: answerIdx,
            };
          }

          if (answer.values) {
            if (typeof answer.values[0] === 'object') {
              const answerItem = answer.values.find(
                ({ value }) => value == answerVal
              );
              if (!answerItem) return;
              this.filter.value = {
                text: answerItem.label,
                value: answerItem.value + '',
              };
            }

            const answerItem = answer.values[answerVal];
            if (!answerItem) return;
            this.filter.value = { text: answerItem, value: answerVal };
          }
        }
      });
    },

    // find name for lessons/campaigns/triggers
    findNameSuggestion(names) {
      let suggestion = '';
      const findSuggestion = (value) => {
        if (typeof value === 'string') {
          return names.find((i) => i.value === value) || value;
        }
        return value;
      };

      if (this.filter.value) {
        if (Array.isArray(this.filter.value)) {
          suggestion = this.filter.value.map((value) => {
            return findSuggestion(value);
          });
        } else {
          suggestion = findSuggestion(this.filter.value);
        }
      }

      if (suggestion) {
        this.filter.value = suggestion;
      }
    },

    selectValue() {
      let modalType = this.isEventRow ? 'FieldSelect' : 'FieldSelectValue';
      this.$emit('showModal', modalType);
    },

    loadEventFieldsCheck(val) {
      if (this.isEventRow) {
        this.$emit('loadEventFields', val);
      }
    },

    setDefaultOperator() {
      let defaultOperatorSign = '$eq';
      const field = this.filter.field;
      const specialFields = ['trigger', 'lesson', 'item', 'campaign'];

      if (field && !specialFields.some((i) => i === field.field)) {
        if (
          ['string', 'list', 'email', 'related_contacts'].includes(field.type)
        ) {
          defaultOperatorSign = '$in';
        } else if (['date', 'time'].includes(field.type)) {
          defaultOperatorSign = '$gt';
        }
      }

      const operator = this.operatorOptions.find((i) => {
        return i.operator === defaultOperatorSign;
      });

      if (operator) {
        this.changeOperator(operator);
      }
    },

    changeOperator(operator, leaveCurrentValue = false) {
      const leaveValue = leaveCurrentValue || this.isEventRow;
      this.filter.operator = operator;

      if (leaveValue) {
        if (!Array.isArray(this.filter.value) && operator.list) {
          this.filter.value = [this.filter.value];
        }
        return;
      }

      if (operator.operator === '$exists') {
        this.filter.value = operator.value === '$exists';
      } else {
        this.filter.value = this.filter.operator.list ? [] : '';
      }
    },

    findOperator() {
      const { operator, value } = this.filter;

      return this.operatorOptions.find((i) => {
        if ((operator === '$exists' && value === 'false') || value === false) {
          return i.value === '^$exists';
        }

        if (i.value === operator) {
          return true;
        }

        if (i.operator === operator) {
          const isRelative = !isNaN(+value);
          const isExpression =
            typeof value === 'string' && value.startsWith('~');

          if (isExpression) {
            return i.expression;
          }

          if (isRelative) {
            return i.relative;
          }

          return true;
        }

        return false;
      });
    },

    initFilterRow() {
      const { operator } = this.filter;

      if (operator && typeof operator === 'string') {
        this.filter.operator = this.findOperator();

        if (this.filter?.operator?.list && !Array.isArray(this.filter.value)) {
          this.filter.value = this.filter.value.split(',');
        }
      }

      this.checkSuggestionsForEvents();
      this.checkNames();
    },

    checkSuggestionsForEvents() {
      if (!this.filter.field || this.filter.field.field !== 'event') return;

      if (this.eventType === 'event') {
        this.suggestions = this.preparedFields
          .filter((i) => i.is_event)
          .map((i) => i.field);
        return;
      }

      this.suggestions = [this.filter.value];
    },
  },
  mounted() {
    this.initFilterRow();
  },
};
</script>

<style lang="less">
.switch-operator {
  margin: 12px 0 0 !important;

  .v-input--selection-controls__input {
    order: 2;
    margin: 0 0 0 12px;
  }

  .v-label {
    font-size: 14px;
    font-weight: 600;
  }
}

.flex-row {
  display: flex;
  justify-content: space-between;

  & > div {
    max-width: calc(50% - 8px);
    width: 100%;
    margin-bottom: 0 !important;

    &.mb-3 {
      margin-bottom: 0 !important;
    }

    &:first-child:last-child {
      max-width: 100%;
    }
  }
}

.group-filter-block {
  position: relative;

  .field__inner-title {
    display: none;
  }
}

.delete-field-btn {
  position: absolute !important;
  top: 6px;
  right: 6px;
}

.operator-select__option {
  height: 40px;
  min-height: 40px;
}

.v-select-list .v-subheader {
  font-style: italic;
  font-weight: bold;
  color: var(--v-appMain-base);
}
</style>
